import React, { useContext, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Alert, Card, CardContent, Typography,
    Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider
} from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';
import { ActiveTeamContext } from '../../context/ActiveTeamContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Carpool from '../../components/termine/Carpool';
import TerminHeader from '../../components/termine/TerminHeader';
import AttendanceTabs from '../../components/termine/AttendanceTabs';
import GastspielerSection from '../../components/termine/GastspielerSection';

const TerminDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeTeamId } = useContext(ActiveTeamContext);

    const { data: profile } = useDataFetch({ url: '/users/profile' });
    const { data, isLoading, error, refetch } = useDataFetch({
        url: activeTeamId ? `/termine/${id}/details?teamId=${activeTeamId}` : null
    });
    const { postData: rsvpStatus } = usePostDataFetch();

    // Gastspieler Fetch & Post
    const { data: gastData, refetch: refetchGast } = useDataFetch({ url: `/teams/${activeTeamId}/gastspieler` });
    const { postData: createGast } = usePostDataFetch();

    const [tabValue, setTabValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [newGastName, setNewGastName] = useState('');

    // RSVP Notiz-Dialog State
    const [notizDialog, setNotizDialog] = useState({ open: false, pendingVariant: null });
    const [notizText, setNotizText] = useState('');

    const isTrainer = profile?.user?.role === 'trainer' || profile?.user?.isAdmin || profile?.user?.role === 'super_admin' ||
        profile?.teams?.find(t => t.team_id.toString() === activeTeamId?.toString())?.rolle === 'trainer';

    const handleTrainerRsvp = async (targetUserId, variant) => {
        await rsvpStatus({
            url: "/termine/participate",
            aBody: { termin_id: id, user_id: targetUserId, variant, is_trainer_override: true },
            skipAuth: false
        });
        refetch();
    };

    const handleUserRsvp = (variant) => {
        if (data?.appointment?.mein_status === variant) return;
        setNotizText('');
        setNotizDialog({ open: true, pendingVariant: variant });
    };

    const confirmUserRsvp = async () => {
        const variant = notizDialog.pendingVariant;
        setNotizDialog({ open: false, pendingVariant: null });

        await rsvpStatus({
            url: "/termine/participate",
            aBody: { termin_id: id, variant, user_id: profile?.user?.id, notiz: notizText || null },
            skipAuth: false
        });
        refetch();
    };

    const handleAddGast = async () => {
        if (!newGastName) return;
        await createGast({
            url: `/teams/${activeTeamId}/gastspieler`,
            aBody: { name: newGastName }
        });
        setNewGastName('');
        refetchGast();
    };

    const filteredGroups = useMemo(() => {
        if (!data?.groups) return null;
        const filter = (list) => list.filter(u =>
            `${u.first_name} ${u.last_name} ${u.alias || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return {
            zusagen: filter(data.groups.zusagen),
            absagen: filter(data.groups.absagen),
            vielleicht: filter(data.groups.vielleicht),
            keine_rueckmeldung: filter(data.groups.keine_rueckmeldung)
        };
    }, [data, searchTerm]);

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data?.appointment) return <Alert severity="info">Termin nicht gefunden.</Alert>;

    const { appointment, counts } = data;

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 1000, mx: 'auto', mb: 10 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Zurück</Button>

            <TerminHeader
                appointment={appointment}
                isTrainer={isTrainer}
                activeTeamId={activeTeamId}
                terminId={id}
            />

            <Card sx={{ mb: 4, borderRadius: "12px", boxShadow: 2 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Mein Status</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant={appointment.mein_status === 'zusage' ? 'contained' : 'outlined'}
                            color="success"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => handleUserRsvp('zusage')}
                            sx={{ borderRadius: "12px", flex: 1, py: 1.5 }}
                        >
                            Zusage
                        </Button>
                        <Button
                            variant={appointment.mein_status === 'absage' ? 'contained' : 'outlined'}
                            color="error"
                            startIcon={<HighlightOffIcon />}
                            onClick={() => handleUserRsvp('absage')}
                            sx={{ borderRadius: "12px", flex: 1, py: 1.5 }}
                        >
                            Absage
                        </Button>
                        <Button
                            variant={appointment.mein_status === 'vielleicht' ? 'contained' : 'outlined'}
                            color="warning"
                            startIcon={<HelpOutlineIcon />}
                            onClick={() => handleUserRsvp('vielleicht')}
                            sx={{ borderRadius: "12px", flex: 1, py: 1.5 }}
                        >
                            Vielleicht
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <AttendanceTabs
                counts={counts}
                filteredGroups={filteredGroups}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                tabValue={tabValue}
                setTabValue={setTabValue}
                isTrainer={isTrainer}
                onTrainerRsvp={handleTrainerRsvp}
            />

            {isTrainer && (
                <GastspielerSection
                    gastData={gastData}
                    newGastName={newGastName}
                    setNewGastName={setNewGastName}
                    handleAddGast={handleAddGast}
                />
            )}

            <Card sx={{ mt: 4, borderRadius: "12px" }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Mitfahrgelegenheiten</Typography>
                    <Carpool terminId={id} />
                </CardContent>
            </Card>

            {/* Notiz-Dialog */}
            <Dialog
                open={notizDialog.open}
                onClose={() => setNotizDialog({ open: false, pendingVariant: null })}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {notizDialog.pendingVariant === 'zusage' ? '✅ Zusage' :
                        notizDialog.pendingVariant === 'absage' ? '❌ Absage' : '❓ Vielleicht'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Möchtest du eine kurze Notiz hinterlassen? (optional)
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="z.B. Komme etwas später..."
                        value={notizText}
                        onChange={(e) => setNotizText(e.target.value)}
                        inputProps={{ maxLength: 200 }}
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNotizDialog({ open: false, pendingVariant: null })} color="inherit" size="small">
                        Abbrechen
                    </Button>
                    <Button onClick={confirmUserRsvp} variant="contained" size="small">
                        Bestätigen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TerminDetails;
